import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { QuadraticBezierLine } from "@react-three/drei";
import { easing } from "maath";
import { useRef } from "react";
import { FogExp2, Vector3, type Group } from "three";
import { SCALE, camEye, camLook, emptyCam, type PulseCam } from "../engine/types";
import type { SpatialNode } from "../engine/types";
import { PLANETS, type Planet } from "../scene/planets";
import { SpaceBackdrop } from "../scene/SpaceBackdrop";
import { WorldPlanet, lookFromPlanet } from "../scene/WorldPlanet";
import { SignalNodeLabel } from "../scene/SignalNodeLabel";
import { isMobile } from "../engine/device";
import { useEngineStore } from "../store/engineStore";
import { useNodeLabelStore } from "../ui/nodeLabelStore";

const cam = emptyCam();

function CameraRig({ fieldActive }: { fieldActive: boolean }) {
  const { camera } = useThree();
  const look = useRef(new Vector3());
  const goal = useRef(new Vector3());
  useFrame((_s, delta) => {
    if (!fieldActive) cam.targetYaw += delta * 0.08;
    easing.damp(cam, "x", cam.targetX, 0.18, delta);
    easing.damp(cam, "y", cam.targetY, 0.18, delta);
    easing.damp(cam, "z", cam.targetZ, 0.18, delta);
    easing.damp(cam, "zoom", cam.targetZoom, 0.22, delta);
    easing.damp(cam, "yaw", cam.targetYaw, 0.2, delta);
    easing.damp(cam, "pitch", cam.targetPitch, 0.2, delta);
    const at = camLook(cam);
    const eye = camEye(cam);
    goal.current.set(eye.x, eye.y, eye.z);
    easing.damp3(camera.position, goal.current, 0.22, delta);
    look.current.set(at.x, at.y, at.z);
    camera.lookAt(look.current);
  });
  return null;
}

function FogSoft() {
  const { scene } = useThree();
  useFrame(() => {
    if (!(scene.fog instanceof FogExp2)) scene.fog = new FogExp2("#02040a", 0.008);
  });
  return null;
}

function WorldBody({ node, focused }: { node: SpatialNode; focused: boolean }) {
  const wrap = useRef<Group>(null);
  const lineRef = useRef<{
    setPoints: (a: [number, number, number], b: [number, number, number], m?: [number, number, number]) => void;
    material?: { dashOffset: number };
  } | null>(null);

  useFrame(({ clock }) => {
    const x = node.x / SCALE;
    const y = -node.y / SCALE;
    const z = node.z / SCALE;
    if (wrap.current) wrap.current.position.set(x, y, z);
    if (!node.parentId) return;
    const px = node.parentX / SCALE;
    const py = -node.parentY / SCALE;
    const pz = node.parentZ / SCALE;
    const dx = x - px;
    const dz = z - pz;
    const len = Math.hypot(dx, y - py, dz) || 1;
    const nlen = Math.hypot(-dz, dx) || 1;
    const nx = -dz / nlen;
    const nz = dx / nlen;
    const arc = Math.max(0.55, len * 0.12);
    lineRef.current?.setPoints(
      [px, py, pz],
      [x, y, z],
      [(px + x) / 2 + nx * arc, (py + y) / 2 + 1.6, (pz + z) / 2 + nz * arc],
    );
    if (lineRef.current?.material) lineRef.current.material.dashOffset = -clock.elapsedTime * 1.4;
  });

  const color =
    node.orbitLayer === "inner" ? "#7df9ff" : node.orbitLayer === "middle" ? "#f0bd68" : "#ca8cff";

  return (
    <group>
      {node.parentId ? (
        <QuadraticBezierLine
          ref={lineRef as never}
          start={[0, 0, 0]}
          end={[0, 0, 0]}
          color={color}
          lineWidth={2.4}
          dashed
          dashScale={8}
          dashSize={0.7}
          gapSize={0.25}
          transparent
          opacity={0.5}
          raycast={() => null}
        />
      ) : null}
      <group ref={wrap}>
        <mesh>
          <sphereGeometry args={[Math.max(0.22, node.look.radius * 0.42), 16, 12]} />
          <meshBasicMaterial color={node.look.atmosphere} />
        </mesh>
        <WorldPlanet
          look={node.look}
          label={node.name}
          showLabel={false}
          growing
          spawnAt={node.spawnedAt}
          focused={focused}
          detail={focused ? (isMobile() ? 24 : 40) : isMobile() ? 16 : 28}
        />
        <SignalNodeLabel node={node} />
      </group>
    </group>
  );
}

function Graph() {
  const nodes = useEngineStore((s) => s.nodes);
  const focusId = useEngineStore((s) => s.focusId);
  useFrame(() => {
    useEngineStore.getState().step();
  });
  return (
    <>
      <FogSoft />
      {nodes.map((node) => (
        <WorldBody key={node.id} node={node} focused={node.id === focusId} />
      ))}
    </>
  );
}

function IdleWorlds() {
  const ingest = useEngineStore((s) => s.ingest);
  const pulse = PLANETS.find((p) => p.id === "pulse");
  const onPlanet = (p: Planet) => {
    void ingest(p.pulseQuery, {
      x: p.position[0] * SCALE,
      y: -p.position[1] * SCALE,
      z: p.position[2] * SCALE,
      look: lookFromPlanet(p, 0.95),
    });
  };
  return (
    <>
      {pulse &&
        PLANETS.filter((p) => p.id !== "pulse").map((p) => (
          <QuadraticBezierLine
            key={`hub-${p.id}`}
            start={pulse.position}
            end={p.position}
            mid={[
              (pulse.position[0] + p.position[0]) / 2,
              (pulse.position[1] + p.position[1]) / 2 + 2,
              (pulse.position[2] + p.position[2]) / 2,
            ]}
            color="#445577"
            lineWidth={2}
            transparent
            opacity={0.28}
            dashed
            dashScale={10}
            dashSize={0.5}
            gapSize={0.35}
            raycast={() => null}
          />
        ))}
      {PLANETS.map((p) => (
        <group key={p.id} position={p.position}>
          <WorldPlanet
            look={lookFromPlanet(p)}
            label={p.name}
            eyebrow={p.eyebrow}
            detail={isMobile() ? 28 : 56}
            onClick={() => onPlanet(p)}
          />
        </group>
      ))}
    </>
  );
}

export function VoidCanvas() {
  const fieldActive = useEngineStore((s) => s.nodes.length > 0);
  const expand = useEngineStore((s) => s.expand);
  const select = useEngineStore((s) => s.select);
  const zoomOut = useEngineStore((s) => s.zoomOut);
  const nodes = useEngineStore((s) => s.nodes);
  const drag = useRef({ down: false, x: 0, y: 0, startX: 0, startY: 0, moved: false });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; zoom: number } | null>(null);
  const mobile = isMobile();

  return (
    <div
      className="void-stage"
      style={{ pointerEvents: fieldActive ? "auto" : "none", touchAction: "none" }}
      onPointerDown={(e) => {
        useNodeLabelStore.getState().hover(null);
        e.currentTarget.style.cursor = "grabbing";
        if (fieldActive) e.currentTarget.setPointerCapture(e.pointerId);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 2) {
          drag.current.moved = true;
          const pts = [...pointers.current.values()];
          pinch.current = {
            dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
            zoom: cam.targetZoom,
          };
          return;
        }
        drag.current = { down: true, x: e.clientX, y: e.clientY, startX: e.clientX, startY: e.clientY, moved: false };
      }}
      onPointerMove={(e) => {
        if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.current.size === 2 && pinch.current) {
          const pts = [...pointers.current.values()];
          const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          cam.targetZoom = Math.max(0.08, Math.min(5, pinch.current.zoom * (d / Math.max(24, pinch.current.dist))));
          drag.current.moved = true;
          return;
        }
        if (!drag.current.down) {
          if (fieldActive && e.pointerType !== "touch" && e.buttons === 0) {
            const hit = pickNode(nodes, e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), cam);
            useNodeLabelStore.getState().hover(hit?.id ?? null);
            e.currentTarget.style.cursor = hit ? "pointer" : "grab";
          }
          return;
        }
        const dx = e.clientX - drag.current.x;
        const dy = e.clientY - drag.current.y;
        if (Math.hypot(e.clientX - drag.current.startX, e.clientY - drag.current.startY) > (mobile ? 18 : 8)) drag.current.moved = true;
        cam.targetYaw -= dx * (mobile ? 0.006 : 0.005);
        cam.targetPitch = Math.max(-1.15, Math.min(1.15, cam.targetPitch + dy * (mobile ? 0.005 : 0.004)));
        drag.current.x = e.clientX;
        drag.current.y = e.clientY;
      }}
      onPointerCancel={(e) => {
        useNodeLabelStore.getState().hover(null);
        e.currentTarget.style.cursor = "grab";
        pointers.current.delete(e.pointerId);
        pinch.current = null;
        drag.current.down = false;
        drag.current.moved = true;
      }}
      onPointerUp={(e) => {
        e.currentTarget.style.cursor = "grab";
        pointers.current.delete(e.pointerId);
        if (pointers.current.size < 2) pinch.current = null;
        const wasDrag = drag.current.moved || pointers.current.size > 0;
        drag.current.down = false;
        if (wasDrag || !fieldActive) return;
        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
        const hit = pickNode(nodes, e.clientX, e.clientY, rect, cam);
        if (hit) {
          select(hit.id);
          cam.targetX = hit.x;
          cam.targetY = hit.y;
          cam.targetZ = hit.z;
          const hasKids = nodes.some((n) => n.parentId === hit.id);
          if (!hasKids) void expand(hit.id);
        } else {
          zoomOut();
        }
      }}
      onWheel={(e) => {
        useNodeLabelStore.getState().hover(null);
        const next = cam.targetZoom * Math.exp(e.deltaY * -0.001);
        cam.targetZoom = Math.max(0.08, Math.min(5, next));
      }}
      onPointerLeave={(e) => {
        useNodeLabelStore.getState().hover(null);
        e.currentTarget.style.cursor = "grab";
      }}
    >
      <Canvas gl={{ antialias: !mobile, alpha: false }} camera={{ position: [12, 7, 20], fov: mobile ? 58 : 50, near: 0.1, far: 4000 }} dpr={mobile ? [1, 1] : [1, 1.6]} style={{ pointerEvents: fieldActive ? "none" : "auto" }}>
        <CameraRig fieldActive={fieldActive} />
        <SpaceBackdrop lite={mobile} />
        {!fieldActive && <IdleWorlds />}
        <Graph />
      </Canvas>
    </div>
  );
}

function pickNode(nodes: SpatialNode[], cx: number, cy: number, rect: DOMRect, c: PulseCam) {
  const fov = (50 * Math.PI) / 180;
  const f = rect.height / 2 / Math.tan(fov / 2);
  const eye = camEye(c);
  const at = camLook(c);
  const fx = at.x - eye.x;
  const fy = at.y - eye.y;
  const fz = at.z - eye.z;
  const fl = Math.hypot(fx, fy, fz) || 1;
  const fwx = fx / fl;
  const fwy = fy / fl;
  const fwz = fz / fl;
  const rx = -fwz;
  const rz = fwx;
  const rl = Math.hypot(rx, rz) || 1;
  const rwx = rx / rl;
  const rwz = rz / rl;
  const uwx = 0 * fwz - rwz * fwy;
  const uwy = rwz * fwx - rwx * fwz;
  const uwz = rwx * fwy - 0 * fwx;
  let best: SpatialNode | null = null;
  let bestD = Infinity;
  for (const node of nodes) {
    const wx = node.x / SCALE - eye.x;
    const wy = -node.y / SCALE - eye.y;
    const wz = node.z / SCALE - eye.z;
    const depth = wx * fwx + wy * fwy + wz * fwz;
    if (depth < 0.2) continue;
    const px = rect.width / 2 + ((wx * rwx + wz * rwz) / depth) * f;
    const py = rect.height / 2 - ((wx * uwx + wy * uwy + wz * uwz) / depth) * f;
    const d = Math.hypot(cx - rect.left - px, cy - rect.top - py);
    const hitPx = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches ? 64 : 48;
    if (d < hitPx && d < bestD) {
      bestD = d;
      best = node;
    }
  }
  return best;
}
